import { STRIPE_CONNECT_SUPPORTED_COUNTRIES } from "../types/iosModels";

export {
  STRIPE_CONNECT_SUPPORTED_COUNTRIES,
  STRIPE_CONNECT_SUPPORTED_COUNTRIES as WORLD_COUNTRIES,
};

export const normalizeStripeConnectCountryName = (value: string): string => {
  return value
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const supportedCountryAliases: Array<[string, string]> = [
  ["AE", "United Arab Emirates"],
  ["AG", "Antigua and Barbuda"],
  ["AL", "Albania"],
  ["AM", "Armenia"],
  ["AR", "Argentina"],
  ["AT", "Austria"],
  ["AU", "Australia"],
  ["BA", "Bosnia and Herzegovina"],
  ["BE", "Belgium"],
  ["BG", "Bulgaria"],
  ["BH", "Bahrain"],
  ["BJ", "Benin"],
  ["BN", "Brunei"],
  ["BO", "Bolivia"],
  ["BS", "Bahamas"],
  ["BW", "Botswana"],
  ["CA", "Canada"],
  ["CH", "Switzerland"],
  ["CI", "Cote d'Ivoire"],
  ["CL", "Chile"],
  ["CO", "Colombia"],
  ["CR", "Costa Rica"],
  ["CY", "Cyprus"],
  ["CZ", "Czech Republic"],
  ["DE", "Germany"],
  ["DK", "Denmark"],
  ["DO", "Dominican Republic"],
  ["EC", "Ecuador"],
  ["EE", "Estonia"],
  ["EG", "Egypt"],
  ["ES", "Spain"],
  ["ET", "Ethiopia"],
  ["FI", "Finland"],
  ["FR", "France"],
  ["GB", "United Kingdom"],
  ["GH", "Ghana"],
  ["GM", "Gambia"],
  ["GR", "Greece"],
  ["GT", "Guatemala"],
  ["GY", "Guyana"],
  ["HK", "Hong Kong"],
  ["HR", "Croatia"],
  ["HU", "Hungary"],
  ["IE", "Ireland"],
  ["IL", "Israel"],
  ["IS", "Iceland"],
  ["IT", "Italy"],
  ["JM", "Jamaica"],
  ["JO", "Jordan"],
  ["JP", "Japan"],
  ["KE", "Kenya"],
  ["KH", "Cambodia"],
  ["KR", "South Korea"],
  ["KW", "Kuwait"],
  ["LC", "Saint Lucia"],
  ["LK", "Sri Lanka"],
  ["LT", "Lithuania"],
  ["LU", "Luxembourg"],
  ["LV", "Latvia"],
  ["MA", "Morocco"],
  ["MC", "Monaco"],
  ["MD", "Moldova"],
  ["MG", "Madagascar"],
  ["MK", "North Macedonia"],
  ["MN", "Mongolia"],
  ["MO", "Macao SAR China"],
  ["MT", "Malta"],
  ["MU", "Mauritius"],
  ["MX", "Mexico"],
  ["NA", "Namibia"],
  ["NG", "Nigeria"],
  ["NL", "Netherlands"],
  ["NO", "Norway"],
  ["NZ", "New Zealand"],
  ["OM", "Oman"],
  ["PA", "Panama"],
  ["PE", "Peru"],
  ["PH", "Philippines"],
  ["PK", "Pakistan"],
  ["PL", "Poland"],
  ["PT", "Portugal"],
  ["PY", "Paraguay"],
  ["QA", "Qatar"],
  ["RO", "Romania"],
  ["RS", "Serbia"],
  ["RW", "Rwanda"],
  ["SA", "Saudi Arabia"],
  ["SE", "Sweden"],
  ["SG", "Singapore"],
  ["SI", "Slovenia"],
  ["SK", "Slovakia"],
  ["SN", "Senegal"],
  ["SV", "El Salvador"],
  ["TH", "Thailand"],
  ["TN", "Tunisia"],
  ["TR", "Turkey"],
  ["TT", "Trinidad and Tobago"],
  ["TW", "Taiwan"],
  ["TZ", "Tanzania"],
  ["UY", "Uruguay"],
  ["UZ", "Uzbekistan"],
  ["VN", "Vietnam"],
  ["ZA", "South Africa"],
  ["Antigua & Barbuda", "Antigua and Barbuda"],
  ["Bosnia & Herzegovina", "Bosnia and Herzegovina"],
  ["Cote d Ivoire", "Cote d'Ivoire"],
  ["Cote d'Ivoire", "Cote d'Ivoire"],
  ["Cote divoire", "Cote d'Ivoire"],
  ["Côte d’Ivoire", "Cote d'Ivoire"],
  ["Czechia", "Czech Republic"],
  ["Hong Kong SAR China", "Hong Kong"],
  ["Macau", "Macao SAR China"],
  ["Macao", "Macao SAR China"],
  ["St Lucia", "Saint Lucia"],
  ["St. Lucia", "Saint Lucia"],
  ["Trinidad & Tobago", "Trinidad and Tobago"],
  ["Turkiye", "Turkey"],
  ["Türkiye", "Turkey"],
  ["UK", "United Kingdom"],
  ["USA", "United States"],
  ["US", "United States"],
];

const supportedCountryNameByNormalizedName = new Map<string, string>();
const supportedCountryCodeByNormalizedName = new Map<string, string>();

STRIPE_CONNECT_SUPPORTED_COUNTRIES.forEach((country) => {
  supportedCountryNameByNormalizedName.set(
    normalizeStripeConnectCountryName(country),
    country
  );
});

supportedCountryAliases.forEach(([alias, country]) => {
  supportedCountryNameByNormalizedName.set(
    normalizeStripeConnectCountryName(alias),
    country
  );
  if (/^[A-Z]{2}$/.test(alias)) {
    supportedCountryCodeByNormalizedName.set(
      normalizeStripeConnectCountryName(alias),
      alias
    );
    supportedCountryCodeByNormalizedName.set(
      normalizeStripeConnectCountryName(country),
      alias
    );
  }
});

export const getStripeConnectSupportedCountryName = (
  value: string | null | undefined
): string | null => {
  const normalized = normalizeStripeConnectCountryName(value || "");
  return normalized ? supportedCountryNameByNormalizedName.get(normalized) || null : null;
};

export const isStripeConnectSupportedCountry = (
  value: string | null | undefined
): boolean => {
  return Boolean(getStripeConnectSupportedCountryName(value));
};

export const getStripeConnectSupportedCountryCode = (
  value: string | null | undefined
): string | null => {
  const normalized = normalizeStripeConnectCountryName(value || "");
  return normalized ? supportedCountryCodeByNormalizedName.get(normalized) || null : null;
};
